import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle, Star, Plugs, CurrencyDollar } from "@phosphor-icons/react";

export default function Cameras() {
  const [cams, setCams] = useState([]);
  useEffect(() => { api.get("/catalog/cameras").then((r) => setCams(r.data)); }, []);

  return (
    <div className="space-y-4">
      <div className="bg-[#121214] border border-white/10 p-4">
        <h2 className="font-display font-black uppercase tracking-tighter text-sm mb-1">Compatible Cameras</h2>
        <p className="text-xs text-[#71717A] leading-relaxed max-w-3xl">
          SentryVision ingests any camera that exposes an <span className="text-[#3395FF] font-mono-data">RTSP</span> stream or <span className="text-[#3395FF] font-mono-data">ONVIF</span> profile — the AI pulls frames from the stream, so on-camera analytics are optional. Below are vetted models by budget and use-case, with pros & cons. <span className="text-[#32D74B]">Recommended</span> picks balance price, image quality, and clean RTSP access.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {cams.map((c) => (
          <div key={`${c.brand}-${c.model}`} data-testid={`camera-card-${c.model}`} className="bg-[#121214] border border-white/10 flex flex-col">
            <div className="relative aspect-video overflow-hidden border-b border-white/10">
              <img src={c.image} alt={c.model} className="w-full h-full object-cover opacity-80" />
              <div className="absolute inset-0 bg-black/30" />
              {c.recommended && (
                <span className="absolute top-2 left-2 flex items-center gap-1 font-mono-data text-[10px] px-1.5 py-0.5 bg-[#32D74B] text-black uppercase tracking-wider">
                  <Star size={11} weight="fill" /> Recommended
                </span>
              )}
              <span className="absolute bottom-2 right-2 font-mono-data text-[10px] px-1.5 py-0.5 bg-black/70 border border-white/20">{c.type}</span>
            </div>

            <div className="p-4 flex-1 flex flex-col">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#71717A]">{c.brand}</p>
                  <h3 className="font-display font-bold tracking-tight">{c.model}</h3>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-0.5 font-mono-data text-lg"><CurrencyDollar size={15} className="text-[#32D74B]" />{c.price_usd}</div>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mt-3 font-mono-data text-[10px]">
                <span className="px-1.5 py-0.5 bg-[#1A1A1E] border border-white/10">{c.resolution}</span>
                <span className="px-1.5 py-0.5 bg-[#1A1A1E] border border-white/10 flex items-center gap-1"><Plugs size={11} /> {c.protocol}</span>
              </div>
              <p className="text-xs text-[#A1A1AA] mt-2">Best for: {c.best_for}</p>

              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-white/10">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#32D74B] mb-1">Pros</p>
                  <ul className="space-y-1">
                    {c.pros.map((p, i) => <li key={i} className="text-xs text-[#A1A1AA] flex gap-1"><span className="text-[#32D74B]">+</span>{p}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-[#FF9F0A] mb-1">Cons</p>
                  <ul className="space-y-1">
                    {c.cons.map((p, i) => <li key={i} className="text-xs text-[#A1A1AA] flex gap-1"><span className="text-[#FF9F0A]">−</span>{p}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-[#007AFF]/10 border border-[#007AFF]/40 p-4 flex items-start gap-3">
        <CheckCircle size={20} weight="fill" className="text-[#3395FF] shrink-0 mt-0.5" />
        <p className="text-sm text-[#E4E4E7] leading-relaxed">
          <span className="font-bold">Buying tip:</span> For a single small shop, start with 3–4 <span className="font-mono-data">Reolink RLC-1224A</span> (perimeter) + <span className="font-mono-data">Dahua TiOC</span> (deterrence at entrance). For multi-aisle retail with loss-prevention focus, choose <span className="font-mono-data">Hikvision AcuSense</span> domes. Pair everything with a PoE switch + NVR, and ensure RTSP is enabled so SentryVision can pull frames.
        </p>
      </div>
    </div>
  );
}
